import ApiService from '@services/api.service'

// ════════════════════════════════════════════════════════════════
// Types de suivi entreprise (référentiel en base)
// ════════════════════════════════════════════════════════════════
// Remplace la liste "entrepriseSuiviType" codée en dur.
//
// COMPATIBILITÉ : le getter garde le même nom et la même forme que
// l'ancienne liste ({ id, name }), donc tous les appels existants
// continuent de fonctionner sans modification, par exemple :
//   mixins.$getStoreLabel($store.getters.getEntrepriseSuiviType(), suivi.type_suivi)
//
// ⚠ Module NON namespacé, comme entrepriseSuivis : il faut SUPPRIMER
// l'ancien getter getEntrepriseSuiviType (et la liste) de l'autre
// module, sinon Vuex lève "duplicate getter key".
//
// Les erreurs sont relancées (sans Notify) : le formulaire de création
// de type affiche les violations 422 sous le bon champ
// ("Ce type de suivi existe déjà").

const RESOURCE = 'entreprise_suivi_types'

const unwrap = (data) => (Array.isArray(data) ? data : (data?.['hydra:member'] ?? []))

const normaliser = (t) => ({
  id: t.id,
  iri: t['@id'] ?? `/${RESOURCE}/${t.id}`,
  name: t.name ?? '',
  disabled: !!t.disabled,
  // Type commun (cfa NULL) : non modifiable
  commun: t.commun ?? t.is_commun ?? false
})

const trier = (a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' })

// Requête en cours : plusieurs composants montés en même temps ne
// déclenchent qu'un seul appel
let chargementEnCours = null

export const entrepriseSuiviTypes = {
  state: {
    entrepriseSuiviTypes: [],
    entrepriseSuiviTypesCharges: false
  },

  mutations: {
    setEntrepriseSuiviTypes (state, types) {
      state.entrepriseSuiviTypes = types
      state.entrepriseSuiviTypesCharges = true
    },
    upsertEntrepriseSuiviType (state, type) {
      const index = state.entrepriseSuiviTypes.findIndex(t => t.id === type.id)
      const liste = [...state.entrepriseSuiviTypes]
      if (index === -1) liste.push(type)
      else liste.splice(index, 1, type)
      state.entrepriseSuiviTypes = liste.sort(trier)
    }
  },

  actions: {
    // GET :: types visibles par l'utilisateur (communs + ceux de son CFA,
    // le filtrage est fait par l'API). Chargés une seule fois ;
    // { force: true } pour recharger.
    fetchEntrepriseSuiviTypes ({ commit, state }, { force = false } = {}) {
      if (chargementEnCours) return chargementEnCours
      if (state.entrepriseSuiviTypesCharges && !force) {
        return Promise.resolve(state.entrepriseSuiviTypes)
      }

      chargementEnCours = ApiService.getRequest(RESOURCE)
        .then((response) => {
          const types = unwrap(response.data).map(normaliser).sort(trier)
          commit('setEntrepriseSuiviTypes', types)
          return types
        })
        .finally(() => { chargementEnCours = null })

      return chargementEnCours
    },

    // POST :: réservé aux superviseurs (contrôlé par l'API).
    // Le CFA est fixé côté serveur : on n'envoie que le nom.
    async addEntrepriseSuiviType ({ commit }, { name }) {
      const response = await ApiService.postRequest(RESOURCE, { name })
      const type = normaliser(response.data)
      commit('upsertEntrepriseSuiviType', type)
      return type
    },

    // PATCH :: renommer, désactiver / réactiver.
    // Types communs : refusé par l'API.
    async updateEntrepriseSuiviType ({ commit }, { id, payload }) {
      const response = await ApiService.patchRequest(`${RESOURCE}/${id}`, payload)
      const type = normaliser(response.data)
      commit('upsertEntrepriseSuiviType', type)
      return type
    }
  },

  getters: {
    /**
     * TOUS les types (actifs et désactivés).
     * Pour AFFICHER le libellé d'un suivi existant : un suivi ancien peut
     * avoir un type désactivé depuis (ex. "Mise à disposition de matériel").
     * Même nom et même forme que l'ancienne liste en dur.
     */
    getEntrepriseSuiviType: state => () => state.entrepriseSuiviTypes,

    /**
     * Types ACTIFS : pour les listes de choix des formulaires (on ne
     * propose pas un type désactivé). En modification, ajouter le type
     * actuel du suivi s'il est désactivé.
     */
    getEntrepriseSuiviTypesActifs: state => () =>
      state.entrepriseSuiviTypes.filter(t => !t.disabled),

    getEntrepriseSuiviTypesCharges: state => state.entrepriseSuiviTypesCharges
  }
}
